/** SUNTRONIX ONE — Operational ONE Orders workspace.
 *
 * Route: /commercial/one-orders
 * Permanent operational orders created from validated + ACCEPTED
 * Customer POs.  Displays the 3-way link Quotation → CPO → ONE Order
 * via the Order 360 tab.
 */
import LifecycleTimeline from "@/components/app/LifecycleTimeline";
import OrderFulfilment from "@/pages/commercial/OrderFulfilment";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { VtigerTable } from "@/components/app/VtigerTable";
import FulfilmentPanel from "@/components/orders/FulfilmentPanel";
import SmartAttachPanel from "@/components/orders/SmartAttachPanel";
import { StatusPill } from "@/components/app/vibe/StatusPill";
import EstablishmentBadge from "@/components/app/EstablishmentBadge";
import { SendWhatsAppButton } from "@/components/whatsapp/WaComposer";
import { DispatchWhatsAppButton } from "@/components/dispatch/DispatchWhatsAppDialog";
import { PageHeader, PrimaryAction } from "@/components/os/PagePattern";
import {
  ClipboardList, ChevronLeft, RefreshCw, Search, Clock, FileText,
  Plus, Upload, Send, AlertTriangle, Receipt, CheckCircle2, Circle,
  Truck, IndianRupee,
} from "lucide-react";
import { QuickActionGroup } from "@/components/quickactions/QuickActionGroup";
import { EntityAddressStrip } from "@/components/address/AddressVerify";
import { OrderFilters, PaymentBadge } from "@/components/orders/OrderFilters";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

const TONE = {
  CREATED:              "bg-emerald-100 text-emerald-800 border-emerald-200",
  IN_FULFILLMENT: "bg-violet-100 text-violet-800 border-violet-200",
  READY_TO_SHIP:  "bg-amber-100 text-amber-800 border-amber-200",
  SHIPPED:              "bg-amber-100 text-amber-800 border-amber-200",
  DELIVERED:          "bg-emerald-100 text-emerald-800 border-emerald-200",
  INVOICED:            "bg-cyan-100 text-cyan-800 border-cyan-200",
  CLOSED:                "bg-slate-100 text-slate-700 border-slate-200",
  CANCELLED:         "bg-rose-100 text-rose-800 border-rose-200",
};

export default function OneOrdersPage() {
  const [rows, setRows] = useState([]);
  const [rollup, setRollup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [invQueue, setInvQueue] = useState([]);
  const [invNos, setInvNos] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) {
      setSelected(openId);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInvQueue = () => api.get("/one-orders/queues/invoicing")
    .then(({ data }) => setInvQueue(data.items || [])).catch(() => {});
  useEffect(() => { loadInvQueue(); }, []);

  const markInvoiced = async (o) => {
    const inv = (invNos[o.id] || "").trim();
    if (!inv) return toast.error("Enter the invoice number");
    try {
      await api.post(`/one-orders/${o.id}/mark-invoiced`, { invoice_number: inv });
      toast.success(`${o.code} marked invoiced (${inv})`);
      loadInvQueue();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Deep-link query params — /commercial/one-orders?status=CREATED[&open=<id>][&customer_id=<id>]
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const st = sp.get("status");
    if (st) setStatusFilter(st);
    if (sp.get("dc") === "1") { setStatusFilter("IN_FULFILLMENT"); toast.info("Pick an order to start its Delivery Challan — the workspace opens on Fulfilment."); }
    else if (sp.get("dc")) {  // legacy draft deep link → the order's Fulfilment tab with that existing DC in focus (read-only lookup)
      const dcid = sp.get("dc");
      api.get(`/delivery-challans/${dcid}`).then(({ data }) => { const oid = data?.order_id || data?.challan?.order_id; if (oid) navigate(`/orders/${oid}?tab=fulfilment&dc=${dcid}`, { replace: true }); else toast.error("Delivery Challan not linked to an order"); })
        .catch(() => toast.error("Delivery Challan not found in the selected company"));
    }
  }, [navigate]);
  const listCustomerId = new URLSearchParams(window.location.search).get("customer_id");

  const [adv, setAdv] = useState({});
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (statusFilter !== "all") params.set("status", statusFilter);
      Object.entries(adv).forEach(([k, v]) => v && params.set(k, v));
      const [list, r] = await Promise.all([
        api.get(`/one-orders?${params}`),
        api.get(`/one-orders/rollup`),
      ]);
      setRows((list.data.items || []).filter((r) =>
          !listCustomerId || r.customer_account_id === listCustomerId ||
                                              r.customer_master_company_id === listCustomerId));
      setRollup(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }, [search, statusFilter, listCustomerId, adv]);
  useEffect(() => { load(); }, [load]);

  const states = rollup?.states || [];
  const counts = rollup?.by_status || {};

  return (
    <div className="space-y-4 max-w-[1500px]" data-testid="one-orders-page">
      <PageHeader eyebrow="Orders & Dispatch" title="Orders" description="Orders created from accepted customer POs. Open an order to see its fulfilment progress and the one next step."
        primary={<PrimaryAction to="/logistics/fulfilment" icon={Truck} testid="oo-open-fulfilment">Fulfilment & Dispatch</PrimaryAction>}
        secondary={<Button variant="outline" onClick={load} disabled={busy} data-testid="oo-refresh"><RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} /> Refresh</Button>} />
      <QuickActionGroup module="orders" />

      <div className="grid grid-cols-3 md:grid-cols-8 gap-2" data-testid="oo-rollup">
        <Tile label="Total" value={rollup?.total ?? 0} />
        {states.map((s) => (
          <Tile key={s} label={s.replace("_", " ")}
            value={counts[s]?.count || 0}
            tone={TONE[s]?.includes("emerald") ? "ok"
                    : TONE[s]?.includes("rose")   ? "err"
                    : TONE[s]?.includes("amber")  ? "warn"
                    : TONE[s]?.includes("violet") ? "info" : undefined} />
        ))}
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-[420px]">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
          <Input placeholder="Search STX-ORD / customer / PO # / quote #…"
            className="pl-8 h-9" value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="oo-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <FilterChip active={statusFilter === "all"}
            onClick={() => setStatusFilter("all")} label="All" />
          {states.map((s) => (
            <FilterChip key={s} active={statusFilter === s}
              onClick={() => setStatusFilter(s)}
              label={s.replace("_", " ")} />
          ))}
        </div>
      </div>

      <OrderFilters value={adv} onChange={setAdv} />

      {invQueue.length > 0 && (
        <div className="border border-violet-200 bg-violet-50/50 rounded-md p-3" data-testid="invoicing-queue">
          <div className="text-[11px] font-semibold text-violet-900 mb-2">
            Invoicing queue — {invQueue.length} delivered order(s) awaiting invoice</div>
          <div className="space-y-1.5">
            {invQueue.map((o) => (
              <div key={o.id} className="bg-white border border-violet-100 rounded px-2.5 py-1.5 flex items-center gap-3 flex-wrap text-[11px]"
                data-testid={`inv-queue-row-${o.code}`}>
                <button onClick={() => setSelected(o.id)} className="font-mono font-semibold text-sky-700">{o.code}</button>
                <span className="text-slate-600 flex-1 truncate">{o.customer_name}</span>
                {o.customer_po_number && <span className="text-slate-400">PO {o.customer_po_number}</span>}
                <span className="font-mono">{fmtINR(o.grand_total_inr)}</span>
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${o.has_signed_dn ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                  {o.has_signed_dn ? "SIGNED DN ON FILE" : "NO SIGNED DN"}</span>
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${o.overdue ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-600"}`}
                  data-testid={`aging-badge-${o.code}`}>
                  {o.overdue ? `⚠ ${o.days_pending}d OVERDUE` : `${o.days_pending}d pending`}</span>
                <input value={invNos[o.id] || ""} placeholder="Invoice #"
                  onChange={(e) => setInvNos({ ...invNos, [o.id]: e.target.value })}
                  data-testid={`inv-no-input-${o.code}`}
                  className="h-7 px-2 text-[11px] font-mono border border-slate-300 rounded w-32" />
                <button onClick={() => markInvoiced(o)} data-testid={`mark-invoiced-btn-${o.code}`}
                  className="h-7 px-2.5 text-[11px] bg-violet-700 text-white rounded">Mark Invoiced</button>
              </div>))}
          </div>
        </div>)}

      <VtigerTable testid="one-orders-table" rows={rows} pageSize={20}
        viewKey="one-orders" onRowClick={(r) => navigate(`/orders/${r.id}${new URLSearchParams(window.location.search).get("dc") === "1" ? "?tab=fulfilment" : ""}`)}
        columns={[
          { key: "code", label: "Order #", mono: true, width: 170,
            render: (r) => (
              <span className="inline-flex items-center gap-1.5">
                <span className="font-mono font-semibold text-sky-700">{r.code}</span>
                {r.is_split && <Badge className="text-[9px] border bg-amber-50 text-amber-800 border-amber-200">SPLIT</Badge>}
              </span>) },
          { key: "payment_status", label: "Payment", width: 110, value: (r) => r.payment_status || "", render: (r) => <PaymentBadge status={r.payment_status} code={r.code} /> },
          { key: "status", label: "Status", width: 130,
            render: (r) => <StatusPill status={r.status} /> },
          { key: "establishment_id", label: "Entity", width: 90, sortable: false,
            render: (r) => <EstablishmentBadge id={r.establishment_id} /> },
          { key: "customer_name", label: "Customer",
            render: (r) => (
              <span>{r.customer_name}
                {r.customer_code && <span className="text-slate-500 ml-1.5">({r.customer_code})</span>}
              </span>),
            value: (r) => `${r.customer_name || ""} ${r.customer_code || ""}` },
          { key: "customer_po_number", label: "Customer PO", mono: true, width: 130 },
          { key: "quotation_code", label: "Quote #", mono: true, width: 120 },
          { key: "created_at", label: "Created", width: 100,
            value: (r) => (r.created_at || "").slice(0, 10) },
          { key: "required_delivery_date", label: "RDD", width: 100,
            value: (r) => (r.required_delivery_date || "").slice(0, 10) },
          { key: "lines", label: "Lines", align: "right", num: true, width: 60,
            filter: false, value: (r) => (r.items || []).length },
          { key: "grand_total_inr", label: "Order value", align: "right", num: true, width: 120,
            value: (r) => r.grand_total_inr, render: (r) => fmtINR(r.grand_total_inr) },
        ]} />

      {selected && (
        <OneOrder360Drawer id={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function Tile({ label, value, tone }) {
  const tint = tone === "ok" ? "text-emerald-800 bg-emerald-50 border-emerald-200"
             : tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
             : tone === "err" ? "text-rose-800 bg-rose-50 border-rose-200"
             : tone === "info" ? "text-violet-800 bg-violet-50 border-violet-200"
             : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={"border rounded p-2 " + tint}>
      <div className="text-[9px] uppercase tracking-wider truncate">{label}</div>
      <div className="text-lg font-heading font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      className={"px-2.5 py-1 rounded text-[10px] uppercase tracking-wider border " + (
        active ? "border-emerald-500 bg-emerald-50 text-emerald-800"
              : "border-slate-200 text-slate-600 hover:bg-slate-50"
      )}>{label}</button>
  );
}

function OneOrder360Drawer({ id, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post(`/one-orders/${id}/email`, { to: emailTo });
      toast.success(`Order Confirmation emailed to ${r.to}`);
      setEmailOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    finally { setSending(false); }
  };

  const loadOrder = () => api.get(`/one-orders/${id}/360`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(e.response?.data?.detail || "Failed"); onClose(); })
      .finally(() => setBusy(false));
  useEffect(() => { loadOrder(); }, [id]); // eslint-disable-line
  const o = data?.order;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
      data-testid="oo-detail-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-white h-full overflow-auto p-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              {o?.code}
            </div>
            <h2 className="text-2xl font-heading font-semibold mt-1">
              {o?.customer_name || "…"}
            </h2>
            <div className="flex gap-2 mt-2 flex-wrap">
              {o && (
                <StatusPill status={o.status} />
              )}
              {o && <SendWhatsAppButton record={{ kind: "order", id: o.id, code: o.code }} purpose="ORDER" testid="send-whatsapp-order" />}
              {o && (
                <a href={`/lifecycle/order/${o.id}`} className="text-[11px] text-sky-700 hover:underline self-center" data-testid="oo-lifecycle-link">Lifecycle timeline →</a>
              )}
              {o && (
                <Link to={`/orders/${o.id}`} className="text-[11px] font-semibold text-[color:var(--st-accent)] hover:underline self-center" data-testid="oo-open-workspace">Open workspace →</Link>
              )}
              {o?.customer_code && (
                <Badge variant="outline" className="text-[10px]">{o.customer_code}</Badge>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={async () => {
                try {
                  const res = await api.get(`/one-orders/${id}/pdf/confirmation`, { responseType: "blob" });
                  window.open(URL.createObjectURL(res.data), "_blank");
                } catch { toast.error("PDF failed"); }
              }}
              data-testid="oo-pdf-confirmation-btn"
              className="h-8 px-3 text-[11px] bg-emerald-700 text-white rounded">
              Order Confirmation PDF</button>
            <button onClick={async () => {
                try {
                  const res = await api.get(`/one-orders/${id}/pdf/delivery-note`, { responseType: "blob" });
                  window.open(URL.createObjectURL(res.data), "_blank");
                } catch { toast.error("PDF failed"); }
              }}
              data-testid="oo-pdf-delivery-btn"
              className="h-8 px-3 text-[11px] border border-emerald-700 text-emerald-800 rounded">
              Delivery Note PDF</button>
            <button onClick={() => { setEmailTo(o?.customer_email || ""); setEmailOpen(!emailOpen); }}
              data-testid="oo-email-btn"
              className="h-8 px-3 text-[11px] border border-sky-600 text-sky-700 rounded">
              Email to Customer</button>
            <button onClick={onClose} className="text-slate-500 text-[13px] ml-1">Close ✕</button>
          </div>
        </div>

        {o && (
          <div className="mt-4 rounded-md border border-slate-200 p-3" data-testid="oo-lifecycle">
            <LifecycleTimeline kind="order" id={o.id} compact />
          </div>
        )}

        {o && (
          <div className="mt-4 rounded-md border border-slate-200 p-3" data-testid="oo-fulfilment">
            <OrderFulfilment orderId={o.id} />
          </div>
        )}

        {emailOpen && (
          <div className="mt-3 border border-sky-200 bg-sky-50/50 rounded-md p-3 flex items-end gap-2 flex-wrap"
            data-testid="oo-email-panel">
            <div className="flex-1 min-w-[240px]">
              <label className="text-[10px] uppercase text-slate-500 block">Recipient email</label>
              <input value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="customer@company.com" data-testid="oo-email-to"
                className="w-full h-9 px-2 border border-slate-300 rounded text-sm bg-white" />
            </div>
            <button onClick={sendEmail} disabled={sending || !emailTo.includes("@")}
              data-testid="oo-email-send"
              className="h-9 px-4 text-sm bg-sky-700 text-white rounded disabled:opacity-50">
              {sending ? "Sending…" : "Send Confirmation PDF"}</button>
          </div>
        )}

        {busy && <div className="mt-8 text-center text-slate-500 text-sm">Loading…</div>}
        {!busy && data && (
          <>
            {/* 3-way relationship band */}
            <div className="mt-5 border border-slate-200 rounded-md p-3 bg-slate-50/60"
              data-testid="oo-360-band">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                3-way link
              </div>
              <div className="flex flex-col md:flex-row gap-3">
                <RelCard label="Quotation" code={data.quote?.code}
                  sub={data.quote?.subject}
                  meta={data.quote && `Grand ${fmtINR(data.quote.grand_total_inr)}`}
                  href="/commercial/crm/quotations" />
                <div className="text-slate-400 self-center hidden md:block">→</div>
                <RelCard label="Customer PO" code={data.customer_po?.code}
                  sub={data.customer_po?.customer_po_number}
                  meta={data.customer_po
                    && `PO ${fmtINR(data.customer_po.total_amount_inr)} · ${data.customer_po.status || "—"}`}
                  href="/commercial/customer-po" />
                <div className="text-slate-400 self-center hidden md:block">→</div>
                <RelCard label="ONE Order" code={o?.code} sub={o?.customer_name}
                  meta={`Grand ${fmtINR(o?.grand_total_inr)}`} current />
              </div>
            </div>

            {/* Lifecycle tracker */}
            <LifecycleStrip stages={data.lifecycle || []} />

            {o?.status === "CLOSED" && <ClosureBanner summary={o.closure_summary} />}

            {/* Shipment & delivery */}
            <ShipmentSection orderId={id} order={o} />

            {/* Invoicing & receivables */}
            <InvoicesSection orderId={id} data={data} reload={loadOrder} />

            {/* Header details */}
            <div className="mt-4 grid grid-cols-3 gap-3">
              <Info label="Grand total" value={fmtINR(o?.grand_total_inr)} bold />
              <Info label="Subtotal" value={fmtINR(o?.subtotal_inr)} />
              <Info label="Tax" value={fmtINR(o?.tax_inr)} />
              <Info label="Currency" value={o?.currency} />
              <Info label="RDD" value={o?.required_delivery_date
                ? new Date(o.required_delivery_date).toLocaleDateString() : "—"} />
              <Info label="Payment terms" value={o?.payment_terms} />
              <Info label="Delivery terms" value={o?.delivery_terms} />
              <Info label="Delivery location" value={o?.delivery_location} />
              <Info label="Contact" value={o?.customer_contact_name} />
            </div>

            {/* Fulfilment: serial allocation, dispatch, delivery confirmation */}
            <FulfilmentPanel order={o} onChanged={loadOrder} />

            {/* ONE Smart Attach — cross-sell outreach for this order */}
            <div className="mt-5">
              <SmartAttachPanel mode="order" orderId={id} />
            </div>

            {/* Line items */}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Line items ({(o?.items || []).length})
              </div>
              <div className="border border-slate-200 rounded overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="text-left px-2 py-1.5">Code / Part #</th>
                      <th className="text-left px-2 py-1.5">Description</th>
                      <th className="text-right px-2 py-1.5">Qty</th>
                      <th className="text-right px-2 py-1.5">Selling ₹</th>
                      <th className="text-right px-2 py-1.5">Disc %</th>
                      <th className="text-right px-2 py-1.5">Tax %</th>
                      <th className="text-right px-2 py-1.5">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(o?.items || []).map((it, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 font-mono">{it.product_code || it.part_number || "—"}</td>
                        <td className="px-2 py-1.5">{it.product_name}</td>
                        <td className="px-2 py-1.5 text-right">{it.quantity}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmtINR(it.approved_selling_price_inr)}</td>
                        <td className="px-2 py-1.5 text-right">{it.discount_pct || 0}</td>
                        <td className="px-2 py-1.5 text-right">{it.tax_pct || 0}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmtINR(it.line_total_inr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Addresses */}
            <div className="mt-4 grid grid-cols-2 gap-3">
              <AddressCard label="Billing address" value={o?.billing_address} />
              <AddressCard label="Delivery address" value={o?.shipping_address} />
            </div>
            {o?.id && (o?.billing_address || o?.shipping_address) && <div className="mt-2"><EntityAddressStrip entityType="ORDER" entityId={o.id} customerAccountId={o.customer_account_id} /></div>}

            {/* Documents */}
            {(data.documents || []).length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Documents
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.documents.map((d, i) => (
                    <Badge key={i} variant="outline" className="text-[10px]">
                      <FileText className="h-3 w-3 mr-1" /> {d.kind}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Status history */}
            {(o?.status_history || []).length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  Status history
                </div>
                <div className="border border-slate-200 rounded divide-y">
                  {o.status_history.map((h, i) => (
                    <div key={i} className="p-2 text-[11px] flex items-center gap-2">
                      <Clock className="h-3 w-3 text-slate-500" />
                      <Badge className={"text-[9px] border " + (TONE[h.status] || "")}>
                        {h.status}
                      </Badge>
                      <span className="text-slate-500 font-mono">{new Date(h.at).toLocaleString()}</span>
                      <span className="text-slate-500">· {h.by}</span>
                      {h.note && <span className="text-slate-600 italic">— {h.note}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function LifecycleStrip({ stages }) {
  if (!stages.length) return null;
  const tone = {
    done:    { dot: "bg-emerald-500 border-emerald-500 text-white", line: "bg-emerald-400", label: "text-emerald-800" },
    active:  { dot: "bg-amber-400 border-amber-400 text-white",     line: "bg-slate-200",  label: "text-amber-800" },
    pending: { dot: "bg-white border-slate-300 text-slate-400",     line: "bg-slate-200",  label: "text-slate-500" },
    skipped: { dot: "bg-slate-100 border-slate-200 text-slate-300", line: "bg-slate-200",  label: "text-slate-400" },
  };
  return (
    <div className="mt-5 border border-slate-200 rounded-md p-4 bg-white overflow-x-auto" data-testid="oo-lifecycle-strip">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Order lifecycle</div>
      <div className="flex items-start min-w-[560px]">
        {stages.map((s, i) => {
          const t = tone[s.state] || tone.pending;
          return (
            <div key={s.key} className="flex-1 flex flex-col items-center relative" data-testid={`oo-stage-${s.key}`}>
              {i > 0 && <div className={`absolute top-3 right-1/2 left-0 h-0.5 -translate-y-1/2 ${t.line}`} />}
              {i < stages.length - 1 && <div className={`absolute top-3 left-1/2 right-0 h-0.5 -translate-y-1/2 ${(tone[stages[i + 1].state] || tone.pending).line}`} />}
              <div className={`relative z-10 h-6 w-6 rounded-full border-2 flex items-center justify-center ${t.dot}`}>
                {s.state === "done" ? <CheckCircle2 className="h-3.5 w-3.5" />
                  : s.state === "active" ? <Circle className="h-2.5 w-2.5 fill-current" />
                  : <Circle className="h-2.5 w-2.5" />}
              </div>
              <div className={`mt-1.5 text-[10px] font-semibold uppercase tracking-wide ${t.label}`}>{s.label}</div>
              {s.ref && <div className="text-[9px] text-slate-500 text-center mt-0.5 leading-tight max-w-[120px] truncate" title={s.ref}>{s.ref}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const INV_TONE = {
  draft: "bg-slate-100 text-slate-600", issued: "bg-sky-100 text-sky-800",
  sent: "bg-sky-100 text-sky-800", partially_paid: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800", overdue: "bg-rose-100 text-rose-800",
  void: "bg-slate-100 text-slate-400", credited: "bg-violet-100 text-violet-800",
};

export function InvoicesSection({ orderId, data, reload }) {
  const b = data.billing || {};
  const cur = b.currency || "INR";
  const payments = data.payments || [];
  const paidTotal = data.paid_total || 0;
  const money = (n) => `${cur} ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  const [recordOpen, setRecordOpen] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const [payFor, setPayFor] = useState(null);

  const uploadPdf = async (invId, file) => {
    if (!file) return;
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/invoice-centre/${invId}/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Tally PDF attached");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  return (
    <div className="mt-5" data-testid="oo-invoices-section">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <Receipt className="h-3.5 w-3.5" /> Invoicing &amp; receivables
        </div>
        <button onClick={() => setRecordOpen(true)} data-testid="oo-record-invoice-btn"
          className="h-7 px-2.5 text-[11px] bg-emerald-700 text-white rounded flex items-center gap-1">
          <Plus className="h-3.5 w-3.5" /> Record Tally invoice
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-2">
        <div className="border rounded p-2 bg-slate-50 border-slate-200" data-testid="oo-bill-ordered">
          <div className="text-[9px] uppercase tracking-wider text-slate-500">Ordered</div>
          <div className="text-[13px] font-mono font-semibold mt-0.5">{money(b.ordered)}</div>
        </div>
        <div className="border rounded p-2 bg-sky-50 border-sky-200" data-testid="oo-bill-invoiced">
          <div className="text-[9px] uppercase tracking-wider text-sky-700">Invoiced ({b.invoice_count || 0})</div>
          <div className="text-[13px] font-mono font-semibold mt-0.5 text-sky-900">{money(b.invoiced)}</div>
        </div>
        <div className={`border rounded p-2 ${b.over_invoiced ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="oo-bill-remaining">
          <div className={`text-[9px] uppercase tracking-wider ${b.over_invoiced ? "text-rose-700" : "text-emerald-700"}`}>
            {b.over_invoiced ? "Over-invoiced" : "Remaining"}
          </div>
          <div className={`text-[13px] font-mono font-semibold mt-0.5 ${b.over_invoiced ? "text-rose-900" : "text-emerald-900"}`}>
            {money(Math.abs(b.remaining))}
          </div>
        </div>
      </div>

      {b.over_invoiced && (
        <div className="mb-2 flex items-center gap-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2.5 py-1.5"
          data-testid="oo-over-invoice-warning">
          <AlertTriangle className="h-3.5 w-3.5" />
          Invoiced total exceeds the order value by {money(Math.abs(b.remaining))}.
        </div>
      )}

      {(b.invoices || []).length > 0 ? (
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-[11px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-2 py-1.5">Invoice #</th>
                <th className="text-left px-2 py-1.5">Date</th>
                <th className="text-right px-2 py-1.5">Amount</th>
                <th className="text-left px-2 py-1.5">Status</th>
                <th className="text-right px-2 py-1.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {b.invoices.map((iv) => (
                <tr key={iv.id} className="border-t border-slate-100" data-testid={`oo-inv-row-${iv.invoice_number}`}>
                  <td className="px-2 py-1.5 font-mono font-semibold">{iv.invoice_number}</td>
                  <td className="px-2 py-1.5 text-slate-500">{iv.invoice_date}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{`${iv.currency || cur} ${Number(iv.grand_total || 0).toLocaleString("en-IN")}`}</td>
                  <td className="px-2 py-1.5">
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase ${INV_TONE[iv.status] || "bg-slate-100 text-slate-600"}`}>{iv.status}</span>
                    {iv.sent_at && <span className="ml-1 text-[9px] text-emerald-600">✓ sent</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <label className="inline-flex items-center gap-1 text-sky-700 cursor-pointer mr-2" title="Attach Tally PDF">
                      <Upload className="h-3.5 w-3.5" />
                      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                        data-testid={`oo-inv-upload-${iv.invoice_number}`}
                        onChange={(e) => uploadPdf(iv.id, e.target.files?.[0])} />
                    </label>
                    {["issued", "sent", "partially_paid", "overdue"].includes(iv.status) && (
                      <button onClick={() => setPayFor(iv)} title="Record payment"
                        data-testid={`oo-inv-pay-${iv.invoice_number}`}
                        className="inline-flex items-center gap-1 text-amber-700 mr-2">
                        <IndianRupee className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button onClick={() => setEmailFor(iv)} title="Email invoice copy"
                      data-testid={`oo-inv-email-${iv.invoice_number}`}
                      className="inline-flex items-center gap-1 text-emerald-700">
                      <Send className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-[11px] text-slate-400 border border-dashed border-slate-200 rounded px-3 py-4 text-center">
          No invoices recorded yet. Raise the invoice in Tally, then record it here.
        </div>
      )}

      {payments.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Payments received · {money(paidTotal)}
          </div>
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5">Date</th>
                  <th className="text-left px-2 py-1.5">Invoice</th>
                  <th className="text-left px-2 py-1.5">Method</th>
                  <th className="text-left px-2 py-1.5">Reference</th>
                  <th className="text-right px-2 py-1.5">Amount</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`oo-pay-row-${p.id}`}>
                    <td className="px-2 py-1.5 text-slate-500">{p.payment_date}</td>
                    <td className="px-2 py-1.5 font-mono">{p.invoice_number}</td>
                    <td className="px-2 py-1.5">{p.method}</td>
                    <td className="px-2 py-1.5 text-slate-500">{p.reference || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{`${p.currency || cur} ${Number(p.amount || 0).toLocaleString("en-IN")}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {recordOpen && (
        <RecordInvoiceDialog orderId={orderId} billing={b} terms={b.payment_terms || data.payment_terms || data.order?.payment_terms || ""}
          onClose={() => setRecordOpen(false)}
          onDone={() => { setRecordOpen(false); reload(); }} />
      )}
      {emailFor && (
        <EmailInvoiceDialog invoice={emailFor} customerEmail={data.order?.customer_email}
          onClose={() => setEmailFor(null)}
          onSent={() => { setEmailFor(null); reload(); }} />
      )}
      {payFor && (
        <PaymentDialog invoice={payFor}
          onClose={() => setPayFor(null)}
          onDone={() => { setPayFor(null); reload(); }} />
      )}
    </div>
  );
}

function ClosureBanner({ summary }) {
  const s = summary || {};
  return (
    <div className="mt-4 border border-emerald-300 bg-emerald-50 rounded-md p-3 flex items-start gap-2.5"
      data-testid="oo-closure-banner">
      <CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
      <div>
        <div className="text-[13px] font-semibold text-emerald-900">Commercially closed</div>
        <div className="text-[11px] text-emerald-800 mt-0.5">
          Delivered, invoiced and paid in full
          {s.closed_at ? ` · ${new Date(s.closed_at).toLocaleDateString()}` : ""}.
          {s.invoiced != null && ` Invoiced ${fmtINR(s.invoiced)}, paid ${fmtINR(s.paid)} across ${s.invoice_count || 0} invoice(s).`}
        </div>
      </div>
    </div>
  );
}

export function ShipmentSection({ orderId, order }) {
  const si = order?.shipment_info || {};
  const dn = order?.signed_delivery_note;
  const dispatched = order?.dispatched_at || si.dispatched_at;
  const hasAny = si.courier || si.awb || dispatched || order?.delivered_at || dn;
  const viewPod = async () => {
    try {
      const res = await api.get(`/one-orders/${orderId}/signed-delivery-note`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Could not open signed delivery note"); }
  };
  if (!hasAny) return null;
  return (
    <div className="mt-5" data-testid="oo-shipment-section">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
        <Truck className="h-3.5 w-3.5" /> Shipment &amp; delivery
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Info label="Courier" value={si.courier || "—"} />
        <Info label="AWB / tracking" value={si.awb || "—"} />
        <Info label="Dispatched" value={dispatched ? new Date(dispatched).toLocaleDateString() : "—"} />
        <Info label="Delivered" value={order?.delivered_at ? new Date(order.delivered_at).toLocaleDateString() : "—"} />
      </div>
      {(si.awb || dispatched) && !order?.delivered_at && (
        <div className="mt-2 flex items-center gap-2">
          <DispatchWhatsAppButton kind="order" id={orderId} label={order?.dispatch_notice?.state === "ACCEPTED" ? "Dispatch WhatsApp sent ✓ · view" : "Send WhatsApp dispatch update"} testid="oo-dispatch-wa" />
          {order?.dispatch_notice?.customer_receipt && <span className="text-[11px] text-emerald-700" data-testid="oo-receipt-confirmed">Customer receipt confirmed {new Date(order.dispatch_notice.customer_receipt.confirmed_at).toLocaleDateString()}</span>}
        </div>
      )}
      {dn && (
        <button onClick={viewPod} data-testid="oo-view-pod-btn"
          className="mt-2 h-8 px-3 text-[11px] border border-emerald-600 text-emerald-800 rounded inline-flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5" /> View proof of delivery (signed DN)
        </button>
      )}
    </div>
  );
}

function PaymentDialog({ invoice, onClose, onDone }) {
  const cur = invoice.currency || "INR";
  const outstanding = Math.max(0, Number(invoice.grand_total || 0) - Number(invoice.paid_amount || 0));
  const [amount, setAmount] = useState(outstanding || "");
  const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!(Number(amount) > 0)) return toast.error("Amount must be greater than 0");
    setBusy(true);
    try {
      const { data } = await api.post(`/invoice-centre/${invoice.id}/payments`, {
        amount: Number(amount), payment_date: payDate, method,
        reference: reference || null, notes: notes || null });
      toast.success(data.status === "paid" ? "Invoice paid in full" : "Payment recorded");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-md p-5" data-testid="oo-payment-dialog">
        <h3 className="text-lg font-heading font-semibold">Record payment</h3>
        <p className="text-[11px] text-slate-500 mt-0.5">
          {invoice.invoice_number} · outstanding <span className="font-mono font-semibold">{cur} {outstanding.toLocaleString("en-IN")}</span>
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Amount ({cur})</div>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="oo-pay-amount"
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm text-right font-mono" />
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Payment date</div>
            <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)}
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm" />
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Method</div>
            <select value={method} onChange={(e) => setMethod(e.target.value)}
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm">
              {["bank_transfer", "cheque", "cash", "upi", "card", "adjustment", "other"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Reference</div>
            <input value={reference} onChange={(e) => setReference(e.target.value)}
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm" />
          </label>
          <label className="block col-span-2">
            <div className="text-[11px] text-slate-500 mb-1">Notes (optional)</div>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm" />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 text-sm border border-slate-300 rounded">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="oo-pay-submit"
            className="h-9 px-4 text-sm bg-emerald-700 text-white rounded disabled:opacity-50">
            {busy ? "Recording…" : "Record payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecordInvoiceDialog({ orderId, billing, terms, onClose, onDone }) {
  const cur = billing.currency || "INR";
  const [num, setNum] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(billing.remaining > 0 ? billing.remaining : "");
  const [tax, setTax] = useState("");
  const [dueDays, setDueDays] = useState("");  // never guessed: blank → backend uses the order's real payment terms or flags PAYMENT_TERMS_MISSING
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (ack = false) => {
    if (!num.trim()) return toast.error("Enter the Tally invoice number");
    if (!(Number(amount) > 0)) return toast.error("Amount must be greater than 0");
    setBusy(true);
    try {
      const { data } = await api.post(`/one-orders/${orderId}/invoices`, {
        invoice_number: num.trim(), invoice_date: date,
        amount: Number(amount), tax_amount: Number(tax) || 0,
        currency: cur, due_days: dueDays === "" ? null : Number(dueDays),
        notes: notes || undefined, acknowledge_over_invoice: ack,
      });
      toast.success(`Invoice ${data.invoice.invoice_number} recorded`);
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to record";
      if (e?.response?.status === 409 && /over-invoice/i.test(detail)) {
        if (window.confirm(detail)) { setBusy(false); return submit(true); }
      } else {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  const willOver = Number(amount) > (billing.remaining ?? 0) + 0.01;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-lg p-5" data-testid="oo-record-invoice-dialog">
        <h3 className="text-lg font-heading font-semibold">Record Tally invoice</h3>
        <p className="text-[11px] text-slate-500 mt-0.5">
          Invoices are raised in Tally. Record the invoice here to track it against this order.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="block col-span-2">
            <div className="text-[11px] text-slate-500 mb-1">Tally invoice number</div>
            <input value={num} onChange={(e) => setNum(e.target.value)} data-testid="oo-ri-number"
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm font-mono" placeholder="e.g. STX/2026-27/00123" />
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Invoice date</div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm" />
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Payment terms (days)</div>
            <input type="number" value={dueDays} min={0} max={365} onChange={(e) => setDueDays(e.target.value)} placeholder={terms ? `Order terms: ${terms}` : "Not on order — enter agreed days"}
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm" data-testid="inv-due-days" />
            {!terms && dueDays === "" && <div className="mt-1 text-[10.5px] text-amber-700" data-testid="inv-terms-missing">No payment terms on the order/quote — leave blank to record without a due date (reminders wait), or enter the agreed days.</div>}
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Amount incl. tax ({cur})</div>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="oo-ri-amount"
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm text-right font-mono" />
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">of which tax ({cur})</div>
            <input type="number" value={tax} onChange={(e) => setTax(e.target.value)}
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm text-right font-mono" />
          </label>
          <label className="block col-span-2">
            <div className="text-[11px] text-slate-500 mb-1">Notes (optional)</div>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full h-9 px-2 border border-slate-300 rounded text-sm" />
          </label>
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          Remaining to invoice: <span className="font-mono font-semibold">{cur} {Number(billing.remaining || 0).toLocaleString("en-IN")}</span>
        </div>
        {willOver && (
          <div className="mt-2 flex items-center gap-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2.5 py-1.5" data-testid="oo-ri-over-warn">
            <AlertTriangle className="h-3.5 w-3.5" /> This will over-invoice the order — you'll be asked to confirm.
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 text-sm border border-slate-300 rounded">Cancel</button>
          <button onClick={() => submit(false)} disabled={busy} data-testid="oo-ri-submit"
            className="h-9 px-4 text-sm bg-emerald-700 text-white rounded disabled:opacity-50">
            {busy ? "Recording…" : "Record invoice"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailInvoiceDialog({ invoice, customerEmail, onClose, onSent }) {
  const [to, setTo] = useState(customerEmail || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!to.includes("@")) return toast.error("Enter a valid recipient email");
    setBusy(true);
    try {
      const { data } = await api.post(`/invoice-centre/${invoice.id}/send-email`,
        { to, message: message || undefined });
      toast.success(`Invoice copy sent to ${data.to}${data.attached_pdf ? " (PDF attached)" : ""}`);
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-md p-5" data-testid="oo-email-invoice-dialog">
        <h3 className="text-lg font-heading font-semibold">Email invoice copy</h3>
        <p className="text-[11px] text-slate-500 mt-0.5">
          {invoice.invoice_number} · sent from accounts@suntronix.net
          {!invoice.pdf_url && " · attach the Tally PDF first for the copy to include it"}
        </p>
        <label className="block mt-3">
          <div className="text-[11px] text-slate-500 mb-1">Recipient email</div>
          <input value={to} onChange={(e) => setTo(e.target.value)} data-testid="oo-email-inv-to"
            className="w-full h-9 px-2 border border-slate-300 rounded text-sm" placeholder="finance@customer.com" />
        </label>
        <label className="block mt-2">
          <div className="text-[11px] text-slate-500 mb-1">Message (optional)</div>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
            className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 text-sm border border-slate-300 rounded">Cancel</button>
          <button onClick={send} disabled={busy || !to.includes("@")} data-testid="oo-email-inv-send"
            className="h-9 px-4 text-sm bg-emerald-700 text-white rounded disabled:opacity-50">
            {busy ? "Sending…" : "Send copy"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function RelCard({ label, code, sub, meta, href, current }) {
  const Card = (
    <div className={"border rounded p-2 flex-1 " + (
      current ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200 hover:bg-slate-50"
    )}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[13px] font-mono font-semibold mt-0.5">{code || "—"}</div>
      {sub && <div className="text-[11px] text-slate-600 truncate">{sub}</div>}
      {meta && <div className="text-[10px] text-slate-500 mt-0.5">{meta}</div>}
    </div>
  );
  if (!current && href && code) return <Link to={href} className="flex-1">{Card}</Link>;
  return Card;
}

export function Info({ label, value, bold }) {
  return (
    <div className="border rounded p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[13px] mt-0.5 ${bold ? "font-semibold font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

export function AddressCard({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="text-[12px] whitespace-pre-line border rounded p-2">{value || "—"}</div>
    </div>
  );
}
